import { getInitialNamespaces } from "remix-i18next/client";
import { RemixBrowser } from "@remix-run/react";
import YAML from "yaml";
import { startTransition, StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";
import i18n from "./i18n";
import i18next from "i18next";
import { I18nextProvider, initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { PWAInstallationProvider } from "./ui/PWAInstallation";
import { initializeTelemetry } from "./kernel/telemetry/initializeTelemetry.client";

initializeTelemetry({
  enabled: window.ENV.ENABLE_SENTRY,
  environment: window.ENV.ENVIRONMENT,
});

/**
 * Load namespaces:
 * - as defined by each route (as conventionalized by remix-i18next)
 * - defined by each route id
 **/
function getNamespaces() {
  return [...i18n.defaultNS, ...getInitialNamespaces()];
}

function waitForBeforeInstallPromptEvent() {
  return new Promise<Event>((resolve, reject) => {
    window.addEventListener("beforeinstallprompt", (event: Event) => {
      event.preventDefault();
      resolve(event);
    });

    const TIMEOUT_MS = 10_000;
    setTimeout(() => reject(false), TIMEOUT_MS);
  });
}

async function hydrate() {
  const ns = getNamespaces();
  // eslint-disable-next-line import/no-named-as-default-member
  await i18next
    .use(initReactI18next) // Tell i18next to use the react-i18next plugin
    .use(LanguageDetector) // Setup a client-side language detector
    .use(Backend) // Setup your backend
    .init({
      ...i18n, // spread the configuration
      // This function detects the namespaces your routes rendered while SSR use
      ns,
      backend: {
        parse: (data: string) => YAML.parse(data),
        loadPath: "/locales/{{lng}}/{{ns}}.yml",
      },
      detection: {
        // Here only enable htmlTag detection, we'll detect the language only
        // server-side with remix-i18next, by using the `<html lang>` attribute
        // we can communicate to the client the language detected server-side
        order: ["htmlTag"],
        // Because we only use htmlTag, there's no reason to cache the language
        // on the browser, so we disable it
        caches: [],
      },
    });

  startTransition(() => {
    hydrateRoot(
      document,
      <PWAInstallationProvider waitForEvent={waitForBeforeInstallPromptEvent()}>
        <I18nextProvider i18n={i18next}>
          <StrictMode>
            <RemixBrowser />
          </StrictMode>
        </I18nextProvider>
      </PWAInstallationProvider>,
    );
  });
}

if (window.requestIdleCallback) {
  window.requestIdleCallback(hydrate);
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  window.setTimeout(hydrate, 1);
}
